import { BaseText } from '@/src/components/BaseText'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { mapToEnergyTypeDto } from '../../../details/types/adjustment.utils'
import { useLoadPlanningAdjustments, useLoadedStrategyMetadata } from '../../hooks/LoadPlanning.hooks'
import type { LoadPlanningMeasureModalItem } from './LoadPlanningMeasureModal.types'
import { LoadPlanningMeasureModalInfoBox } from './LoadPlanningMeasureModalInfoBox'
import {
  formatOverlappingHolidayTimeframesLabel,
  getInfoSectionLabels,
  getOverlappingHolidayTimeframesForMeasureItem,
} from './LoadPlanningMeasureModalInfoSection.utils'

interface LoadPlanningMeasureModalInfoSectionProps {
  measureItem: LoadPlanningMeasureModalItem
  isCandidate?: boolean
}

export const LoadPlanningMeasureModalInfoSection = ({ measureItem, isCandidate }: LoadPlanningMeasureModalInfoSectionProps) => {
  const { t, i18n } = useTranslation()
  const { currentLocation, companyPgId: companyId } = useLocationsContext()
  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const loadPlanningYear = strategyMetadata?.year
  const loadPlanningEnergyType = strategyMetadata?.energyType
  const locationId = currentLocation?.id ? Number(currentLocation.id) : undefined

  const { data: adjustments } = useLoadPlanningAdjustments({
    companyId,
    locationId,
    energyType: mapToEnergyTypeDto(loadPlanningEnergyType),
    year: loadPlanningYear ? Number(loadPlanningYear) : undefined,
  })

  const simulationWasDeleted = measureItem.simulationChangeStatus === 'DELETED'
  const simulationWasUpdated = measureItem.simulationChangeStatus === 'UPDATED'

  const overlappingHolidayTimeframes = useMemo(() => {
    return getOverlappingHolidayTimeframesForMeasureItem({ measureItem, adjustments, language: i18n.language })
  }, [adjustments, measureItem.id, measureItem.timeframeEnd, measureItem.timeframeStart, measureItem.warnings, i18n.language])

  const overlappingHolidayTimeframesLabel = useMemo(
    () => formatOverlappingHolidayTimeframesLabel(overlappingHolidayTimeframes, i18n.language),
    [overlappingHolidayTimeframes, i18n.language]
  )

  const updatedCandidate = useMemo(() => {
    if (isCandidate || !simulationWasUpdated) return undefined
    if (!measureItem.simulationId) return undefined

    const measureSimulationCandidates = adjustments?.measureSimulationCandidates ?? []
    return measureSimulationCandidates.find(candidate => candidate.id.toString() === measureItem.simulationId)
  }, [adjustments?.measureSimulationCandidates, isCandidate, measureItem.simulationId, simulationWasUpdated])

  const consumptionDeltaUpdated = updatedCandidate && updatedCandidate.consumptionDelta !== measureItem.measureConsumptionDelta
  const effectiveFromDateUpdated = updatedCandidate && updatedCandidate.measureReference?.effectivenessDate !== measureItem.effectiveFrom

  const showSimulationDeletedOrUnlinkedInfo = simulationWasDeleted && !isCandidate
  const showConsumptionDeltaUpdatedInfo = consumptionDeltaUpdated && !effectiveFromDateUpdated
  const showEffectiveFromDateUpdatedInfo = effectiveFromDateUpdated && !consumptionDeltaUpdated
  const showConsumptionDeltaAndEffectiveFromDateUpdatedInfo = consumptionDeltaUpdated && effectiveFromDateUpdated
  const showOverlappingHolidayTimeframesInfo = overlappingHolidayTimeframes.length > 0

  const showDescriptionInfo =
    !showSimulationDeletedOrUnlinkedInfo &&
    !showConsumptionDeltaUpdatedInfo &&
    !showEffectiveFromDateUpdatedInfo &&
    !showOverlappingHolidayTimeframesInfo

  const { updatedAtLabel, effectivenessDateLabel, effectiveFromLabel } = useMemo(
    () =>
      getInfoSectionLabels({
        effectiveFrom: measureItem.effectiveFrom,
        updatedAt: updatedCandidate?.updatedAt,
        effectivenessDate: updatedCandidate?.measureReference?.effectivenessDate,
        language: i18n.language,
      }),
    [measureItem.effectiveFrom, updatedCandidate?.updatedAt, updatedCandidate?.measureReference?.effectivenessDate, i18n.language]
  )

  return (
    <>
      {showOverlappingHolidayTimeframesInfo && (
        <LoadPlanningMeasureModalInfoBox
          text={t(
            measureItem.scope === 'COMPANY'
              ? 'components.procurement.forecasting.measure_modal.holiday_overlap_info_company'
              : 'components.procurement.forecasting.measure_modal.holiday_overlap_info',
            {
              timeframes: overlappingHolidayTimeframesLabel,
            }
          )}
        />
      )}

      {showSimulationDeletedOrUnlinkedInfo && (
        <LoadPlanningMeasureModalInfoBox text={t('components.procurement.forecasting.measure_modal.simulation_change_status_info')} />
      )}

      {showEffectiveFromDateUpdatedInfo && (
        <LoadPlanningMeasureModalInfoBox
          text={t('components.procurement.forecasting.measure_modal.effective_from_updated_info', {
            date: updatedAtLabel,
            fromDate: effectiveFromLabel,
            toDate: effectivenessDateLabel,
          })}
        />
      )}

      {showConsumptionDeltaUpdatedInfo && (
        <LoadPlanningMeasureModalInfoBox
          text={t('components.procurement.forecasting.measure_modal.consumption_delta_updated_info', {
            date: updatedAtLabel,
          })}
        />
      )}

      {showConsumptionDeltaAndEffectiveFromDateUpdatedInfo && (
        <LoadPlanningMeasureModalInfoBox
          text={t('components.procurement.forecasting.measure_modal.consumption_effective_from_updated_info', {
            date: updatedAtLabel,
            fromDate: effectiveFromLabel,
            toDate: effectivenessDateLabel,
          })}
        />
      )}

      {showDescriptionInfo && (
        <BaseText>
          {isCandidate
            ? t('components.procurement.forecasting.measure_modal.description_candidate')
            : t('components.procurement.forecasting.measure_modal.description_adjustment')}
        </BaseText>
      )}
    </>
  )
}
